import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, getEnvironmentRuntime, previewRuntimeAdoption, setEnvironmentRollbackPolicy, setEnvironmentRuntime } from '../../api';
import type { CatalogSearchPolicy, EnvironmentRuntimeView, RollbackPolicy, RuntimeAdoptionPreview, SetEnvironmentRuntimeInput } from '../../api';
import { useResource } from '../../hooks';
import { catalogSearchPolicyLabel } from '../../labels';
import { Callout, Checkbox, Input, Select } from '../primitives';
import { Field, LoadingRows } from '../ui';
import { humanize, rollbackPolicyDescription, rollbackPolicyLabel } from '../../labels';
import { EligibilityBlockers } from './EligibilityBlockers';
import { PanelCard, SectionHeader } from './ui';

/**
 * RuntimeStrategyCard: the environment's runtime settings (CO-159). The
 * current strategy is a value; the drain timeout, what happens when it is
 * reached and asset retention are one short form, each consequence beside
 * its field and the mechanism behind "How deployment switching works".
 * Adopting blue-green runtimes is explicit — the operator reads what each
 * component's next deployment replaces and confirms it. An owner may let a
 * Magento environment apply releases that declare their additive schema
 * change backward compatible without the protected window. What a failed
 * deployment does (the rollback policy) is one select saved with the rest of
 * the card, by the one Save button; the components a blue-green runtime does
 * not serve yet are listed as exceptions.
 */
export function RuntimeStrategyCard({ environmentId, environmentName, isWordPress }: { environmentId: string; environmentName: string; isWordPress: boolean }) {
  // The same read the Overview shows: shared, cached, refreshed in the
  // background. What is saved here replaces it at once (mutate).
  const runtime = useResource(() => getEnvironmentRuntime(environmentId), [environmentId], undefined, { key: `env:${environmentId}:runtime` });
  const view: EnvironmentRuntimeView | null = runtime.data?.runtime ?? null;
  const loading = runtime.loading;
  const setView = (next: EnvironmentRuntimeView) => runtime.mutate?.({ ...runtime.data, runtime: next });
  const [preview, setPreview] = useState<RuntimeAdoptionPreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [form, setForm] = useState<SetEnvironmentRuntimeInput>({});
  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const identity = workspace.data?.identity;
  // The server refuses the change from anyone else; the local development
  // workspace acts as an owner there.
  const isOwner = Boolean(identity?.role?.is_owner_role || identity?.kind === 'local_development_workspace');
  // The same right the runtime settings need; the server refuses anyone else.
  const canChangePolicy = isOwner || Boolean(identity?.capabilities?.includes('manage_deployments'));
  const [policyDraft, setPolicyDraft] = useState<RollbackPolicy | null>(null);

  // Another environment starts clean; the form is filled from the first answer
  // for it, and not again by the background refreshes that follow.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    setPreview(null);
    setConfirmed(false);
    setPolicyDraft(null);
    seededFor.current = null;
  }, [environmentId]);
  useEffect(() => {
    const loaded = runtime.data?.runtime;
    if (!loaded || seededFor.current === environmentId) return;
    seededFor.current = environmentId;
    setForm({
      drain_bound_seconds: loaded.settings.drain_bound_seconds,
      drain_policy: loaded.settings.drain_policy,
      asset_retention_hours: loaded.settings.asset_retention_hours,
      wordpress_managed_immutable: loaded.settings.wordpress_managed_immutable,
      allow_declared_compatible: loaded.settings.allow_declared_compatible,
      read_only_code: Boolean(loaded.settings.read_only_code),
      non_root_storefronts: Boolean(loaded.settings.non_root_storefronts),
      catalog_search_policy: loaded.settings.catalog_search_policy ?? 'warn',
    });
  }, [environmentId, runtime.data]);

  const blueGreen = view?.settings.strategy === 'blue_green';
  const policy: RollbackPolicy | undefined = view?.rollback_policy;
  const selectedPolicy = policyDraft ?? policy;
  const policyChanged = Boolean(policy && selectedPolicy && selectedPolicy !== policy);
  // Components a failed deployment is not switched back for, by the server's
  // own reasons (policy and routed runtime): listed only when it is automatic.
  const notAutomatic = policy === 'automatic_code_rollback_when_safe' && blueGreen ? (view?.components ?? []).filter((c) => c.automatic_rollback && !c.automatic_rollback.applies) : [];

  const decision = view?.default;
  const pendingDefault = !blueGreen && decision?.state === 'pending';

  async function keepRecreate() {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const res = await setEnvironmentRuntime(environmentId, { strategy: 'recreate' });
      setView(res.runtime);
      setSaved('This environment keeps the recreate strategy.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The runtime strategy could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  async function showPreview() {
    setError(null);
    try {
      const res = await previewRuntimeAdoption(environmentId);
      setPreview(res.preview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The adoption preview could not be loaded.');
    }
  }

  // One save for the whole card: a changed rollback policy is written first
  // (its own endpoint), then the runtime settings.
  async function save(event: FormEvent, adopt: boolean) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      if (policyChanged && selectedPolicy) {
        const res = await setEnvironmentRollbackPolicy(environmentId, selectedPolicy);
        if (res.runtime) setView(res.runtime);
        else if (view) setView({ ...view, rollback_policy: res.rollback_policy });
        setPolicyDraft(null);
      }
      const res = await setEnvironmentRuntime(environmentId, {
        ...form,
        drain_policy: isWordPress ? 'stop_after_bound' : form.drain_policy,
        strategy: adopt ? 'blue_green' : view?.settings.strategy,
        confirm_adoption: adopt ? confirmed : undefined,
        // Sent only when changed: an unchanged value never needs an owner.
        allow_declared_compatible: form.allow_declared_compatible !== view?.settings.allow_declared_compatible ? form.allow_declared_compatible : undefined,
        read_only_code: Boolean(form.read_only_code) !== Boolean(view?.settings.read_only_code) ? Boolean(form.read_only_code) : undefined,
        non_root_storefronts: Boolean(form.non_root_storefronts) !== Boolean(view?.settings.non_root_storefronts) ? Boolean(form.non_root_storefronts) : undefined,
        catalog_search_policy: (form.catalog_search_policy ?? 'warn') !== (view?.settings.catalog_search_policy ?? 'warn') ? form.catalog_search_policy : undefined,
      });
      setView(res.runtime);
      setPreview(null);
      setSaved(adopt ? 'Blue-green runtimes adopted. Each component adopts them at its next deployment.' : 'Runtime settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The runtime settings could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const strategyLabel = blueGreen ? 'Blue-green runtimes' : pendingDefault ? 'Blue-green runtimes, once the server allows' : 'Recreate strategy';
  const strategyNote = blueGreen
    ? `Code-only releases switch without a maintenance window${!isWordPress && view?.settings.allow_declared_compatible ? ', and so do releases that declare an additive schema change backward compatible' : ''}.`
    : pendingDefault
      ? 'This new environment will use them if its server’s agent can; that is decided once, when it is first set up or deployed.'
      : 'A deployment switches the release and restarts the component’s services in place.';

  return (
    <PanelCard labelledBy="settings-runtime" testId="runtime-strategy">
      <SectionHeader
        id="settings-runtime"
        title={`Runtime settings for ${environmentName}`}
        help={
          <>
            <p>
              Recreate replaces what serves in place. Blue-green prepares and verifies a new runtime beside the active one
              and switches traffic to it; the old one drains for the drain timeout. Releases that change the database or
              configuration still take a protected window
              {!isWordPress && view?.settings.allow_declared_compatible ? ', unless the release declares an additive schema change backward compatible' : ''}.
            </p>
            <p>Adopting blue-green runtimes is explicit: each component adopts them at its next deployment, and nothing is migrated by saving.</p>
          </>
        }
        helpLabel="How deployment switching works"
      />
      {loading ? (
        <LoadingRows label="Loading the runtime settings" rows={2} rowHeight="40px" />
      ) : !view ? (
        <Callout tone="danger" title="Runtime settings not loaded">
          <p>{error ?? runtime.error?.message ?? 'The runtime settings could not be loaded.'}</p>
          <p>Reload the page to try again.</p>
        </Callout>
      ) : (
        <form className="form-grid runtime-strategy__form" onSubmit={(e) => save(e, false)}>
          <dl className="runtime-strategy__current" data-testid="runtime-strategy-current">
            <dt>Deployment switching</dt>
            <dd>
              <strong>{strategyLabel}</strong>
              <span className="field__hint">{strategyNote}</span>
              {blueGreen && decision?.state === 'applied' ? (
                <span className="field__hint" data-testid="runtime-default-applied">
                  It started on blue-green runtimes as a new environment
                  {decision.decided_at ? ` (${new Date(decision.decided_at).toLocaleDateString()})` : ''}.
                </span>
              ) : null}
            </dd>
          </dl>
          {policy ? (
            <div className="runtime-strategy__policy" data-testid="rollback-policy">
              <Field label="After a failed deployment" hint={selectedPolicy ? rollbackPolicyDescription[selectedPolicy] : undefined}>
                <Select
                  value={selectedPolicy}
                  disabled={!canChangePolicy || saving}
                  onChange={(e) => setPolicyDraft(e.target.value as RollbackPolicy)}
                  data-testid="rollback-policy-select"
                >
                  {(Object.keys(rollbackPolicyLabel) as RollbackPolicy[]).map((value) => (
                    <option key={value} value={value}>
                      {rollbackPolicyLabel[value] ?? humanize(value)}
                    </option>
                  ))}
                </Select>
              </Field>
              {notAutomatic.length > 0 ? (
                <p className="type-meta" id="rollback-not-automatic-lead">Not rolled back automatically</p>
              ) : null}
              {notAutomatic.length > 0 ? (
                <ul className="runtime-list__reasons" data-testid="rollback-not-automatic" aria-labelledby="rollback-not-automatic-lead">
                  {notAutomatic.map((c) => (
                    <li key={c.application_id}>
                      <strong>{c.name}</strong>: {c.automatic_rollback?.reasons.join('; ')}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {!blueGreen && decision?.state === 'declined' ? (
            <Callout tone="info" title="Started on the recreate strategy" testId="runtime-default-declined">
              <p>Blue-green runtimes were not used when this environment was set up: {decision.reason}</p>
            </Callout>
          ) : null}
          <div className="runtime-strategy__fields">
            <Field label="Drain timeout (seconds)" hint="At least your longest request; the edge allows 300 s by default.">
              <Input
                type="number"
                inputMode="numeric"
                min={10}
                max={3600}
                value={form.drain_bound_seconds ?? ''}
                onChange={(e) => setForm({ ...form, drain_bound_seconds: Number(e.target.value) })}
              />
            </Field>
            <Field
              label="When the drain timeout is reached"
              hint={
                isWordPress
                  ? 'WordPress always stops it: the object cache is settled only after the old runtime is stopped.'
                  : 'Either way the result is recorded as a bounded drain, never as graceful.'
              }
            >
              <Select
                value={isWordPress ? 'stop_after_bound' : (form.drain_policy ?? 'keep_running')}
                onChange={(e) => setForm({ ...form, drain_policy: e.target.value as SetEnvironmentRuntimeInput['drain_policy'] })}
              >
                {isWordPress ? null : <option value="keep_running">Keep the old runtime running (no new traffic)</option>}
                <option value="stop_after_bound">Stop the old runtime</option>
              </Select>
            </Field>
            <Field label="Asset retention (hours)" hint="At least your longest HTML cache lifetime (CDN and Varnish).">
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={720}
                value={form.asset_retention_hours ?? ''}
                onChange={(e) => setForm({ ...form, asset_retention_hours: Number(e.target.value) })}
              />
            </Field>
          </div>
          {isWordPress ? (
            <Checkbox
              checked={Boolean(form.wordpress_managed_immutable)}
              onChange={(e) => setForm({ ...form, wordpress_managed_immutable: e.target.checked })}
              label="Managed-immutable WordPress"
              hint="Blocks plugin, theme and core updates and file editing in WP Admin, so updates come only through releases; uploads stay writable."
            />
          ) : null}
          {blueGreen && !isWordPress ? (
            <div className="runtime-strategy__compatible">
              <Checkbox
                data-testid="allow-declared-compatible"
                checked={Boolean(form.allow_declared_compatible)}
                disabled={!isOwner}
                onChange={(e) => setForm({ ...form, allow_declared_compatible: e.target.checked })}
                label="Apply declared backward-compatible database changes without a wait"
                hint={
                  <>
                    A declared change that only adds tables, nullable columns or indexes is applied while the store keeps
                    serving; any other change still takes the protected window.
                    {isOwner ? '' : ' Only an owner can change this.'}
                  </>
                }
              />
              <details className="quiet-disclosure runtime-strategy__compatible-help">
                <summary>How a release declares it</summary>
                <p className="field__help">
                  A release declares in <code translate="no">.commerceops/database-compatibility.json</code> that its schema
                  change keeps the previous release working, for the modules it names. Rollback stays available.
                </p>
              </details>
            </div>
          ) : null}
          {blueGreen && !isWordPress ? (
            <div className="runtime-strategy__hardening">
              <Checkbox
                data-testid="read-only-code"
                checked={Boolean(form.read_only_code)}
                disabled={!isOwner}
                onChange={(e) => setForm({ ...form, read_only_code: e.target.checked })}
                label="Mount Magento code read-only"
                hint="Writable: app/etc, generated, pub, var"
              />
              <Checkbox
                data-testid="non-root-storefronts"
                checked={Boolean(form.non_root_storefronts)}
                disabled={!isOwner}
                onChange={(e) => setForm({ ...form, non_root_storefronts: e.target.checked })}
                label="Run storefronts as a non-root user"
                hint="Installs, builds and Node run unprivileged"
              />
              <Field label="Candidate catalog search fails">
                <Select
                  data-testid="catalog-search-policy"
                  value={form.catalog_search_policy ?? 'warn'}
                  disabled={!isOwner}
                  onChange={(e) => setForm({ ...form, catalog_search_policy: e.target.value as CatalogSearchPolicy })}
                >
                  {(Object.keys(catalogSearchPolicyLabel) as CatalogSearchPolicy[]).map((p) => (
                    <option key={p} value={p}>
                      {catalogSearchPolicyLabel[p]}
                    </option>
                  ))}
                </Select>
              </Field>
              {isOwner ? null : <span className="muted">Only an owner can change these.</span>}
            </div>
          ) : null}
          <div className="runtime-strategy__actions">
            <button type="submit" className="button button--primary" disabled={saving}>
              {saving ? 'Saving' : 'Save runtime settings'}
            </button>
            {pendingDefault ? (
              <button type="button" className="button button--secondary" disabled={saving} onClick={keepRecreate} data-testid="keep-recreate">
                Keep the recreate strategy
              </button>
            ) : null}
            {!blueGreen && !pendingDefault && view.capability.supported && !preview ? (
              <button type="button" className="button button--secondary" onClick={showPreview}>
                Review blue-green adoption
              </button>
            ) : null}
          </div>
          {!blueGreen && !pendingDefault && !view.capability.supported ? (
            <Callout tone="info" title="Blue-green runtimes are not available here yet" testId="runtime-capability-blockers">
              <EligibilityBlockers blockers={view.capability.blockers} fallback={view.capability.reason} />
            </Callout>
          ) : null}
          {preview ? (
            <Callout tone="warning" title="What adopting blue-green runtimes changes" testId="runtime-adoption-preview">
              <ul className="runtime-list__reasons">
                {preview.components.map((c) => (
                  <li key={c.application_id}>
                    <strong>{c.name}</strong>
                    {c.supported ? (
                      <>
                        {c.replaced_services.length > 0 ? `: replaces the compose ${c.replaced_services.join(', ')}` : ''}
                        {c.steps && c.steps.length > 0 ? ` (${c.steps.join('; ')})` : ''}
                      </>
                    ) : c.blockers && c.blockers.length > 0 ? (
                      <EligibilityBlockers blockers={c.blockers} testId={`adoption-blockers-${c.application_id}`} />
                    ) : (
                      <>: {c.reason}</>
                    )}
                  </li>
                ))}
              </ul>
              <ul className="runtime-list__reasons">
                {[...preview.interruption, ...preview.notes].map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="confirm-adoption"
                label="I reviewed what each component's next deployment replaces."
              />
              <div className="runtime-strategy__actions">
                <button type="button" className="button button--primary" disabled={!confirmed || saving} onClick={(e) => save(e as unknown as FormEvent, true)}>
                  Adopt blue-green runtimes
                </button>
                <button type="button" className="button button--secondary" onClick={() => setPreview(null)}>
                  Not now
                </button>
              </div>
            </Callout>
          ) : null}
          {error ? (
            <Callout tone="danger" title="Not saved">
              {error}
            </Callout>
          ) : null}
          {saved ? <Callout tone="success">{saved}</Callout> : null}
        </form>
      )}
    </PanelCard>
  );
}
